/**
 * Module dependencies.
 */

import {
  Box,
  RawHtml,
  color,
  media,
  normalizeNewLine,
  units
} from '@untile/react-components';

import { Loading, Subtitle, Type } from 'src/components/core';
import { ReactElement, ReactNode } from 'react';
import { ifProp, theme } from 'styled-tools';
import Container from 'src/components/core/layout/container';
import Metatags from 'src/components/core/metatags';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  description: string;
  isLarger?: boolean;
  isLoading?: boolean;
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  background-color: ${color('backgrounds.sage02')};
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(6)}) 0;
  position: relative;
  width: 100vw;

  ${media.min('md')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<{ isLarger?: boolean }>`
  ${media.min('md')`
    display: grid;
    grid-column-gap: ${theme('grid.columnsGutter')}px;
    grid-template-areas: '. content .';
    grid-template-columns: repeat(3, 4fr);

    ${ifProp(
      'isLarger',
      css`
        grid-template-columns: 3.5fr 5fr 3.5fr;
      `
    )}
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Subtitle)`
  color: ${color('black')};
  margin-bottom: ${units(4)};
`;

/**
 * `BaseAuthPage` page.
 */

const BaseAuthPage = (props: Props): ReactElement => {
  const { children, className, description, isLarger, isLoading, title } =
    props;

  const animProps = {
    'data-scroll': 'fade-in-up-small',
    'data-scroll-offset': '20%'
  };

  return (
    <>
      <Metatags
        pageTitle={title}
        title={title}
      />

      <Wrapper className={className}>
        <Loading active={isLoading} />

        {!isLoading && (
          <Container>
            <Grid isLarger={isLarger}>
              <Box gridArea={'content'}>
                <Type.H1
                  {...animProps}
                  marginBottom={units(1)}
                >
                  {title}
                </Type.H1>

                <RawHtml
                  {...animProps}
                  element={Description}
                >
                  {normalizeNewLine(description)}
                </RawHtml>

                {children}
              </Box>
            </Grid>
          </Container>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `BaseAuthPage` component.
 */

export default BaseAuthPage;
